import SoftphoneAPI from '@/api/softphone.api'
import {
  SOFTPHONE_EVENTS,
  createSoftphoneTransport,
  exposeSoftphoneDevHook,
  removeSoftphoneDevHook
} from '@/services/softphone'
import SoftphoneTabLock from '@/services/softphone/tabLock'
import { armRingtone, startRinging, stopRinging } from '@/services/softphone/ringtone'
import { goOfflineWithKeepalive } from '@/services/softphone/goOfflineBeacon'
import { createSoftphoneChannel, SOFTPHONE_COMMANDS } from '@/services/softphone/softphoneChannel'
import formatCurrency from '@/utils/formatCurrency'

/**
 * `otherTab` is not part of the call lifecycle: it is where this tab lands when a newer tab
 * claims the softphone, so two widgets can never answer for the same agent.
 */
export const SOFTPHONE_STATUS = Object.freeze({
  OFFLINE: 'offline',
  CONNECTING: 'connecting',
  READY: 'ready',
  RINGING: 'ringing',
  ACTIVE: 'active',
  WRAP_UP: 'wrapUp',
  OTHER_TAB: 'otherTab',
  ERROR: 'error'
})

/**
 * Which window this store is. The widget window OWNS the transport and is the only one that may
 * touch a call; every dashboard tab is a MIRROR that renders the owner's broadcasts and asks the
 * owner to act. A store that has not been told which it is does neither.
 */
export const SOFTPHONE_ROLES = Object.freeze({
  OWNER: 'owner',
  MIRROR: 'mirror'
})

/**
 * Post-call wrap-up. An answered call that runs past WRAP_UP_ELIGIBLE_CALL_DURATION_IN_MS earns the
 * agent a breather: the widget pauses the live campaign server-side while the call is still up, and
 * once it ends holds WRAP_UP_DURATION_IN_MS before resuming the campaign. Shorter calls (and declines)
 * go straight back to Ready - the campaign was never paused, so there is nothing to wait for.
 *
 * The pause happens DURING the call rather than at hang-up so that a call already being routed as
 * the agent hangs up finds the campaign paused, not a widget that is about to stop answering.
 */
export const WRAP_UP_ELIGIBLE_CALL_DURATION_IN_MS = 15000
export const WRAP_UP_DURATION_IN_MS = 60000

const STATUS_LABELS = {
  [SOFTPHONE_STATUS.OFFLINE]: 'Offline',
  [SOFTPHONE_STATUS.CONNECTING]: 'Connecting',
  [SOFTPHONE_STATUS.READY]: 'Ready',
  [SOFTPHONE_STATUS.RINGING]: 'Incoming',
  [SOFTPHONE_STATUS.ACTIVE]: 'On call',
  [SOFTPHONE_STATUS.WRAP_UP]: 'Wrap-up',
  [SOFTPHONE_STATUS.OTHER_TAB]: 'Other tab',
  [SOFTPHONE_STATUS.ERROR]: 'Error'
}

// Transport, tab lock and timers are deliberately module scoped: they are neither serialisable
// nor reactive, and keeping them out of state means strict mode never sees them mutate.
let transport = null
let tabLock = null
let wrapUpTimerId = null
let wrapUpEligibilityTimerId = null

// Set for the duration of `goOffline`. Both transports emit UNREGISTERED from their own
// `disconnect()`, and that event now takes the agent offline itself - without this flag the
// teardown would re-enter itself and pause the campaign a second time.
let isGoingOffline = false
let channel = null

// A mirror can be told to go online before the widget window has finished booting. The command is
// held here and re-sent on the owner's first broadcast, which doubles as "the owner is listening".
let pendingCommand = null

export function getSoftphoneChannel() {
  return channel
}

function clearWrapUpTimer() {
  if (wrapUpTimerId) {
    clearTimeout(wrapUpTimerId)
    wrapUpTimerId = null
  }
}

function clearWrapUpEligibilityTimer() {
  if (wrapUpEligibilityTimerId) {
    clearTimeout(wrapUpEligibilityTimerId)
    wrapUpEligibilityTimerId = null
  }
}

const ONLINE_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.CONNECTING,
  SOFTPHONE_STATUS.READY,
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE,
  SOFTPHONE_STATUS.WRAP_UP
])

const IN_CALL_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE
])

/**
 * Closes the widget window. Split out so a test can assert it without a real window, and so the one
 * place the store reaches for the DOM is obvious.
 */
export function closeOwnWindow() {
  if (typeof window !== 'undefined' && typeof window.close === 'function') {
    window.close()
  }
}

function _ensureChannel() {
  if (!channel) {
    channel = createSoftphoneChannel()
  }

  return channel
}

/**
 * Everything a mirror needs to render. Deliberately a hand-built object rather than the whole state:
 * `role` must never travel, or a mirror would adopt the owner's role and believe it may answer.
 */
export function buildSoftphoneSnapshot(state) {
  return {
    status: state.status,
    liveCampaign: state.liveCampaign,
    incomingCall: state.incomingCall,
    activeCall: state.activeCall,
    wrapUpEndsAt: state.wrapUpEndsAt,
    isPausedForWrapUp: state.isPausedForWrapUp,
    isMuted: state.isMuted,
    lastError: state.lastError
  }
}

export function _broadcastState(state) {
  if (state.role !== SOFTPHONE_ROLES.OWNER || !channel) {
    return
  }

  channel.postState(buildSoftphoneSnapshot(state))
}

const state = {
  // null until becomeOwner/becomeMirror is dispatched. See SOFTPHONE_ROLES.
  role: null,

  status: SOFTPHONE_STATUS.OFFLINE,

  // The single campaign this agent is live for. Only one at a time, by product decision.
  liveCampaign: null,

  incomingCall: null,

  activeCall: null,

  wrapUpEndsAt: null,

  // True from the moment the wrap-up pause landed on the server until the wrap-up resume did. Kept
  // separately from `status` because a second call can arrive and end while it is set, and that
  // call's end must still run the wrap-up - otherwise the campaign would stay paused forever.
  isPausedForWrapUp: false,

  isMuted: false,

  lastError: null
}

/**
 * @type {import('vuex').GetterTree<typeof state>}
 */
const getters = {
  /**
   * ENTITLEMENT SEAM. Every softphone surface is gated on this getter and nothing else. It
   * delegates to the single softphone gate the app already has: the global SOFTPHONE_GOES_LIVE
   * setting AND the per-user `softphoneAgent` flag. Both fail closed, so the incoming-call UI
   * is hidden unless the softphone campaign flows are visible to this user too.
   */
  isSoftphoneAvailable(state, getters, rootState, rootGetters) {
    return rootGetters['globalSettings/isSoftphoneUser'] === true
  },

  isSoftphoneOwner(state) {
    return state.role === SOFTPHONE_ROLES.OWNER
  },

  liveCampaignName(state) {
    return state.liveCampaign?.name || ''
  },

  /**
   * How long an answered call must run before it earns. Zero for a campaign with no minimum, which
   * the widget says in words rather than reporting "0 seconds" - that reads like a value that
   * failed to load.
   */
  liveCampaignMinimumDurationInSeconds(state) {
    if (!state.liveCampaign?.minimumDurationSelected) {
      return 0
    }

    return Math.max(0, Number(state.liveCampaign.minimumDurationInSeconds) || 0)
  },

  /**
   * What a billable call pays, formatted. Empty for a bid of zero: that is a campaign that reached
   * this window without one, and "$0.00" would read as calls that pay nothing.
   */
  liveCampaignBidAmount(state) {
    const bid = Number(state.liveCampaign?.maxBidPerCallInUSD) || 0

    return bid > 0 ? formatCurrency(bid) : ''
  },

  /**
   * The states the live campaign takes calls from, empty when it takes them from all of them. A
   * softphone campaign can only filter on states - the ZIP code list is not offered for one - and
   * filtering on an empty selection is the same campaign as one that does not filter at all.
   */
  liveCampaignStates(state) {
    if (!state.liveCampaign?.searchLocation) {
      return []
    }

    return (state.liveCampaign.locationStates ?? []).filter(Boolean)
  },

  isSoftphoneOnline(state) {
    return ONLINE_STATUSES.includes(state.status)
  },

  isRinging(state) {
    return state.status === SOFTPHONE_STATUS.RINGING
  },

  isCallActive(state) {
    return state.status === SOFTPHONE_STATUS.ACTIVE
  },

  isWrappingUp(state) {
    return state.status === SOFTPHONE_STATUS.WRAP_UP
  },

  liveCampaignId(state) {
    return state.liveCampaign?.id || null
  },

  /**
   * How long an answered call must run before the widget shows the caller's full number; until then
   * it shows only the last four digits.
   *
   * The gate is the live campaign's MINIMUM DURATION - the point at which the call is billable, and
   * so the point at which the buyer has earned the number. A campaign without a minimum duration
   * has nothing to wait for, so `0`: the number is shown from the moment the call is answered.
   */
  callerIdRevealAfterSeconds(state) {
    if (!state.liveCampaign?.minimumDurationSelected) {
      return 0
    }

    return Math.max(0, Number(state.liveCampaign.minimumDurationInSeconds) || 0)
  },

  softphoneStatusLabel(state) {
    return STATUS_LABELS[state.status] || STATUS_LABELS[SOFTPHONE_STATUS.OFFLINE]
  }
}

/**
 * @type {import('vuex').ActionTree<typeof state>}
 */
const actions = {
  /**
   * Claim ownership of the softphone. Called once by the widget window.
   *
   * The tab lock is claimed HERE rather than in `goOnline`, because the thing that must be unique
   * is the widget, not the call: two open widgets would both register and both ring.
   */
  becomeOwner({ commit, dispatch, state }) {
    commit('SET_ROLE', SOFTPHONE_ROLES.OWNER)

    _ensureChannel()

    channel.onHello(() => _broadcastState(state))
    channel.onCommand(message => dispatch('handleRemoteCommand', message))

    if (!tabLock) {
      tabLock = new SoftphoneTabLock()
      tabLock.onLost = () => dispatch('handleTabClaimLost')
    }

    tabLock.claim()

    // Doubles as "the owner is listening" - a mirror holding a queued command sends it on this.
    _broadcastState(state)
  },

  /**
   * Follow the owner. Called once per dashboard tab. A mirror never creates a transport.
   */
  becomeMirror({ commit, dispatch }) {
    commit('SET_ROLE', SOFTPHONE_ROLES.MIRROR)

    _ensureChannel()

    channel.onState(message => dispatch('applyRemoteState', message.snapshot))
    channel.onClosed(() => dispatch('handleOwnerClosed'))

    // The widget may already be up and mid-call; ask where things stand rather than assume offline.
    channel.postHello()
  },

  applyRemoteState({ commit, dispatch, state }, snapshot) {
    if (!snapshot) {
      return
    }

    const didWrapUpPauseChange = Boolean(snapshot.isPausedForWrapUp) !== Boolean(state.isPausedForWrapUp)

    // The agent stopped being live for a campaign: the widget paused it server-side, so the row
    // this tab is showing as Active is now Paused. Covers the agent going offline from the widget
    // and the widget dropping offline by itself (a lost registration, a transport error).
    const didStopBeingLive = Boolean(state.liveCampaign) && !snapshot.liveCampaign

    commit('APPLY_REMOTE_STATE', snapshot)

    // The widget paused or resumed the campaign on the server, and it has no campaigns list to
    // refresh - this tab does. Best effort by construction (the action swallows its own failure).
    if (didWrapUpPauseChange || didStopBeingLive) {
      dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
    }

    if (pendingCommand) {
      const command = pendingCommand

      pendingCommand = null

      channel.postCommand(command.name, command.payload)
    }
  },

  /**
   * The widget shut down CLEANLY - it has already paused the campaign, so there is nothing to do but
   * drop back to offline and make every Go Live button clickable again.
   */
  handleOwnerClosed({ commit, dispatch, state }) {
    pendingCommand = null

    const wasLive = Boolean(state.liveCampaign)

    commit('RESET_SOFTPHONE')

    // The widget paused the campaign on its way out. Only this tab has the list showing that
    // campaign as Active, so only this tab can put the badge right.
    if (wasLive) {
      dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
    }
  },

  /**
   * This tab NOTICED the widget window disappear, rather than being told. Reached when the agent
   * closes the window with the X: a message posted during unload is not reliably delivered, so the
   * tab that opened the window watches the handle instead.
   *
   * It pauses the campaign, because a window that disappeared without sending `closed` did not get
   * to finish: the widget tries on its way out with a keepalive request, but that is the one path
   * that cannot be awaited or retried, and a campaign left Active with no agent behind it routes
   * calls at nobody. Pausing twice is harmless.
   */
  async handleOwnerGone({ commit, dispatch }) {
    pendingCommand = null

    _ensureChannel()

    // Sibling dashboard tabs never had the window handle, so they only learn about it from here.
    channel.postClosed()

    await dispatch('pauseLiveCampaign')

    commit('RESET_SOFTPHONE')
  },

  async handleRemoteCommand({ dispatch }, { name, payload } = {}) {
    if (name === SOFTPHONE_COMMANDS.GO_ONLINE) {
      await dispatch('goOnline', payload)

      return
    }

    if (name === SOFTPHONE_COMMANDS.GO_OFFLINE) {
      await dispatch('teardownSoftphone')

      // The agent asked to stop taking calls, so the window has no reason to stay open. Only the
      // remote path closes: an internal goOffline (a transport error, a lost claim) must leave the
      // window up so the agent can see what happened.
      closeOwnWindow()
    }
  },

  /**
   * Mirror side of Go Live. The window is NOT opened here - a popup opened after an await has lost
   * the user gesture and is blocked, so the caller opens it inside the click handler and resumes the
   * campaign before asking the owner to connect.
   */
  requestGoOnline(context, campaign) {
    _ensureChannel()

    pendingCommand = {
      name: SOFTPHONE_COMMANDS.GO_ONLINE,
      payload: {
        id: campaign.id,
        name: campaign.name,
        // The budget has to travel: the widget window gates the caller id on the minimum duration
        // (see `callerIdRevealAfterSeconds`), shows both terms next to the campaign name, and
        // never fetches campaigns itself. Narrowed to the three fields rather than passing the
        // whole row, because this is structured-cloned across the channel on every go-live.
        budget: {
          minimumDurationSelected: campaign.budget?.minimumDurationSelected === true,
          minimumDurationInSeconds: Number(campaign.budget?.minimumDurationInSeconds) || 0,
          maxBidPerCallInUSD: Number(campaign.budget?.maxBidPerCallInUSD) || 0
        },

        // Which states the campaign takes calls from - shown with the rest of its terms. A
        // softphone campaign filters on states and nothing else, so the two audience fields are
        // all of it.
        audience: {
          searchLocation: campaign.audience?.searchLocation === true,
          selectedLocations: [...(campaign.audience?.selectedLocations ?? [])]
        }
      }
    }

    channel.postCommand(pendingCommand.name, pendingCommand.payload)
  },

  requestGoOffline() {
    _ensureChannel()

    pendingCommand = null

    channel.postCommand(SOFTPHONE_COMMANDS.GO_OFFLINE)
  },

  /**
   * Go live for exactly one campaign. Must be called from a user gesture: the ringtone is
   * armed here (see `ringtone.js`).
   */
  async goOnline({ commit, dispatch, state, rootGetters }, campaign) {
    if (!campaign?.id) {
      throw new Error('A campaign is required to go online')
    }

    // One campaign at a time: going online anywhere else takes the agent offline first.
    if (state.liveCampaign && state.liveCampaign.id !== campaign.id) {
      await dispatch('goOffline')
    } else if (state.liveCampaign?.id === campaign.id && state.status !== SOFTPHONE_STATUS.OFFLINE && state.status !== SOFTPHONE_STATUS.ERROR) {
      return
    }

    commit('SET_LAST_ERROR', null)
    // The budget rides along because the widget window gates the caller id on the minimum duration
    // (see `callerIdRevealAfterSeconds`) and shows the terms of the campaign it is taking calls
    // for, and that window never fetches campaigns itself.
    commit('SET_LIVE_CAMPAIGN', {
      id: campaign.id,
      name: campaign.name,
      minimumDurationSelected: campaign.budget?.minimumDurationSelected === true,
      minimumDurationInSeconds: Number(campaign.budget?.minimumDurationInSeconds) || 0,
      maxBidPerCallInUSD: Number(campaign.budget?.maxBidPerCallInUSD) || 0,
      searchLocation: campaign.audience?.searchLocation === true,
      locationStates: [...(campaign.audience?.selectedLocations ?? [])]
    })
    commit('SET_STATUS', SOFTPHONE_STATUS.CONNECTING)

    await armRingtone()

    try {
      transport = await createSoftphoneTransport()

      transport.on(SOFTPHONE_EVENTS.REGISTERED, () => dispatch('handleRegistered'))
      transport.on(SOFTPHONE_EVENTS.UNREGISTERED, () => dispatch('handleUnregistered'))
      transport.on(SOFTPHONE_EVENTS.INCOMING, payload => dispatch('handleIncomingCall', payload))
      transport.on(SOFTPHONE_EVENTS.ANSWERED, payload => dispatch('handleCallAnswered', payload))
      transport.on(SOFTPHONE_EVENTS.ENDED, payload => dispatch('handleCallEnded', payload))
      transport.on(SOFTPHONE_EVENTS.ERROR, payload => dispatch('handleTransportError', payload))

      exposeSoftphoneDevHook(transport)

      const token = transport.requiresToken
        ? (await SoftphoneAPI.getWebRtcToken(rootGetters['auth/accountId']))?.data?.token
        : null

      await transport.connect(token)
    } catch (error) {
      await dispatch('goOffline')

      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
      commit('SET_LAST_ERROR', error?.response?.data?.title || error?.message || 'Could not start the softphone')

      throw error
    }
  },

  /**
   * Going offline is the OWNER's job end to end, including pausing the campaign. The dashboard tab
   * that resumed it may be gone by now - closing the widget window has to leave the campaign paused
   * whether or not anything else is still open.
   */
  async goOffline({ commit, dispatch }, { isUnloading = false } = {}) {
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()
    stopRinging()

    isGoingOffline = true

    try {
      await dispatch('pauseLiveCampaign', { isUnloading })

      if (transport) {
        const currentTransport = transport

        transport = null

        try {
          await currentTransport.disconnect()
        } catch (error) {
          console.error('Failed to disconnect softphone transport', error)
        }

        currentTransport.removeAllListeners()
        removeSoftphoneDevHook()
      }

      // The tab lock is NOT released here. Going offline leaves the widget window open and still
      // the only one allowed to exist; the lock is held until the window itself goes away.
      commit('RESET_SOFTPHONE')
    } finally {
      isGoingOffline = false
    }
  },

  /**
   * Tells the server the agent is offline, which pauses EVERY softphone campaign ringing them - not
   * just the one this window went live for - so calls stop being routed to a destination with nobody
   * behind it. Best effort: a failure here must not stop the transport from disconnecting, because
   * leaving the agent connected-but-offline is worse than a campaign that stays active.
   *
   * Skipped when this window never went live: it then has nothing to take offline, and a widget that
   * merely opened and closed must not pause campaigns an agent is live for elsewhere.
   */
  async pauseLiveCampaign({ dispatch, state, rootGetters }, { isUnloading = false } = {}) {
    if (!state.liveCampaign?.id) {
      return
    }

    const accountId = rootGetters['auth/accountId']

    // An XHR started in `beforeunload` is cancelled with the page, so the unload path uses fetch
    // with keepalive instead. It skips the axios auth-refresh interceptor - acceptable, because the
    // alternative is not pausing at all, and an expired token means the agent's session was already
    // over. Every other path keeps the interceptor.
    if (isUnloading) {
      goOfflineWithKeepalive(accountId, rootGetters['auth/accessToken'])

      return
    }

    try {
      // The root campaign action rather than SoftphoneAPI directly: importing the api module here
      // creates a cycle (api -> httpClient -> router -> store -> this module) that leaves the store
      // half-built at import time.
      await dispatch('campaign/goOfflineSoftphone', null, { root: true })
    } catch (error) {
      console.error('Failed to take the softphone agent offline', error)
    }
  },

  /**
   * Full teardown for widget-window close. Closing the window counts as going offline.
   */
  async teardownSoftphone({ dispatch, state }, { isUnloading = false } = {}) {
    await dispatch('goOffline', { isUnloading })

    // Announced AFTER the campaign is paused, not before, and that ordering carries meaning: a
    // `closed` message means the widget shut down cleanly and there is nothing left to do. A window
    // that vanishes WITHOUT one is picked up by the opener's watcher, which pauses on its behalf
    // (see handleOwnerGone). Announcing first would make the two indistinguishable and leave a
    // killed widget's campaign Active.
    if (state.role === SOFTPHONE_ROLES.OWNER && channel) {
      channel.postClosed()
    }

    if (tabLock) {
      tabLock.destroy()
      tabLock = null
    }

    if (channel) {
      channel.close()
      channel = null
    }

    pendingCommand = null
  },

  async answerCall({ state }) {
    if (!state.incomingCall || !transport) {
      return
    }

    await transport.answer(state.incomingCall.id)
  },

  async declineCall({ state }) {
    if (!state.incomingCall || !transport) {
      return
    }

    await transport.reject(state.incomingCall.id)
  },

  async hangUp({ state }) {
    if (!state.activeCall || !transport) {
      return
    }

    await transport.hangup(state.activeCall.id)
  },

  async setMuted({ commit }, isMuted) {
    if (transport) {
      await transport.setMuted(isMuted)
    }

    commit('SET_MUTED', isMuted)
  },

  handleRegistered({ commit, state }) {
    if (state.status === SOFTPHONE_STATUS.CONNECTING) {
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)
    }
  },

  /**
   * The registration is gone, so Telnyx has nowhere to send a call - but the campaign is still
   * live, and Ringba would keep routing calls at a destination that cannot answer them. Taking the
   * agent offline is therefore part of handling this event, not just a status change: it pauses the
   * campaign and puts the Go Live buttons back so the agent can reconnect deliberately.
   */
  async handleUnregistered({ commit, dispatch, state }) {
    if (state.status === SOFTPHONE_STATUS.OFFLINE) {
      return
    }

    // Already tearing down - this event IS that teardown's disconnect. RESET_SOFTPHONE at the end
    // of it sets the status; setting it here keeps the old behaviour for the interim.
    if (isGoingOffline) {
      commit('SET_STATUS', SOFTPHONE_STATUS.OFFLINE)

      return
    }

    await dispatch('goOffline')

    // After goOffline, which resets the state: the agent needs to know their campaign is paused
    // and why the widget went quiet.
    commit('SET_LAST_ERROR', 'The softphone lost its connection, so your campaign has been paused. Go live again when you are ready to take calls.')
  },

  /**
   * A call can still land during wrap-up: one that was already being routed when the campaign
   * paused. It is taken like any other, and `isPausedForWrapUp` stays set so its end runs the
   * wrap-up again and the campaign is eventually resumed.
   */
  handleIncomingCall({ commit, state }, { callId, campaignName, callerNumber } = {}) {
    clearWrapUpTimer()

    commit('SET_WRAP_UP_ENDS_AT', null)
    commit('SET_INCOMING_CALL', {
      id: callId,
      // v1 shows the campaign and nothing else; the transport has no campaign of its own.
      campaignName: campaignName || state.liveCampaign?.name || '',
      // Carried but deliberately NOT shown while ringing - the widget only reveals it on an
      // answered call. See `callerId` in SoftphoneWidget.
      callerNumber: callerNumber || null,
      receivedAt: Date.now()
    })
    commit('SET_STATUS', SOFTPHONE_STATUS.RINGING)

    startRinging()
  },

  handleCallAnswered({ commit, dispatch, state }, { callId, callerNumber } = {}) {
    stopRinging()

    commit('SET_ACTIVE_CALL', {
      id: callId || state.incomingCall?.id,
      campaignName: state.incomingCall?.campaignName || state.liveCampaign?.name || '',
      // The ringing call already carried it; the answered event repeats it, so prefer whichever
      // arrived - a call picked up in another window has no local incomingCall to read from.
      callerNumber: callerNumber || state.incomingCall?.callerNumber || null,
      answeredAt: Date.now()
    })
    commit('SET_INCOMING_CALL', null)
    commit('SET_MUTED', false)
    commit('SET_STATUS', SOFTPHONE_STATUS.ACTIVE)

    // A call that is still up this long earns a wrap-up; the campaign is paused now, mid-call, so
    // nothing new is routed at an agent who is about to need a break. See WRAP_UP_* above.
    clearWrapUpEligibilityTimer()
    wrapUpEligibilityTimerId = setTimeout(() => dispatch('pauseForWrapUp'), WRAP_UP_ELIGIBLE_CALL_DURATION_IN_MS)
  },

  /**
   * Pauses the live campaign server-side because the current call has run long enough to earn a
   * wrap-up. Best effort: a failure leaves the call untouched and the agent told, and the call then
   * ends straight into Ready as a short one would.
   */
  async pauseForWrapUp({ commit, dispatch, state }) {
    clearWrapUpEligibilityTimer()

    if (state.status !== SOFTPHONE_STATUS.ACTIVE || state.isPausedForWrapUp || !state.liveCampaign?.id) {
      return
    }

    try {
      await dispatch('campaign/pauseSoftphoneForWrapUp', null, { root: true })
    } catch (error) {
      console.error('Failed to pause the campaign for wrap-up', error)

      commit('SET_LAST_ERROR', 'Could not pause the campaign for wrap-up - the next call may arrive as soon as this one ends')

      return
    }

    // The request was in flight for a while; the world may have moved on. An agent who went offline
    // meanwhile wanted the campaign paused anyway, and the reset already cleared this flag - setting
    // it now would leave a stale "paused for wrap-up" on an offline widget.
    if (!ONLINE_STATUSES.includes(state.status)) {
      return
    }

    commit('SET_PAUSED_FOR_WRAP_UP', true)

    // The call ended while the pause was in flight and handleCallEnded, seeing no pause yet, went
    // back to Ready. The campaign IS paused now, so the wrap-up has to start here or nothing would
    // ever resume it.
    if (state.status === SOFTPHONE_STATUS.READY) {
      dispatch('beginWrapUp')
    }
  },

  handleCallEnded({ commit, dispatch, state }) {
    stopRinging()
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()

    commit('SET_INCOMING_CALL', null)
    commit('SET_ACTIVE_CALL', null)
    commit('SET_MUTED', false)

    if (state.isPausedForWrapUp) {
      dispatch('beginWrapUp')

      return
    }

    // A short call, a decline, a missed call: the campaign was never paused, so the agent is simply
    // eligible for the next call. Anything else (offline, error, another tab) is left as it is.
    if (IN_CALL_STATUSES.includes(state.status) || state.status === SOFTPHONE_STATUS.WRAP_UP) {
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)
    }
  },

  beginWrapUp({ commit, dispatch }) {
    clearWrapUpTimer()

    commit('SET_WRAP_UP_ENDS_AT', Date.now() + WRAP_UP_DURATION_IN_MS)
    commit('SET_STATUS', SOFTPHONE_STATUS.WRAP_UP)

    wrapUpTimerId = setTimeout(() => dispatch('finishWrapUp'), WRAP_UP_DURATION_IN_MS)
  },

  /**
   * End of the wrap-up window: resume the campaign the agent was live on. A resume that fails leaves
   * a paused campaign behind a Ready-looking widget, which is the one state an agent cannot detect
   * from the widget alone, so it is handled like a failed goOnline - the widget goes offline with the
   * error showing and every Go Live button comes back so the agent can go live again.
   */
  async finishWrapUp({ commit, dispatch, state }) {
    clearWrapUpTimer()

    if (state.status !== SOFTPHONE_STATUS.WRAP_UP) {
      return
    }

    commit('SET_WRAP_UP_ENDS_AT', null)

    if (!state.isPausedForWrapUp) {
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)

      return
    }

    try {
      await dispatch('campaign/resumeSoftphoneAfterWrapUp', { campaignId: state.liveCampaign?.id }, { root: true })
    } catch (error) {
      console.error('Failed to resume the campaign after wrap-up', error)

      await dispatch('goOffline')

      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
      commit('SET_LAST_ERROR', error?.response?.data?.title || 'Could not resume the campaign after wrap-up. Go live again to keep taking calls.')

      return
    }

    if (state.status === SOFTPHONE_STATUS.WRAP_UP) {
      commit('SET_PAUSED_FOR_WRAP_UP', false)
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)

      return
    }

    // A call landed while the resume was in flight: the campaign is live again and the call is being
    // handled; only the flag needs clearing.
    if (IN_CALL_STATUSES.includes(state.status)) {
      commit('SET_PAUSED_FOR_WRAP_UP', false)

      return
    }

    // The agent went offline while the resume was in flight. Their go-offline and this go-live raced
    // on the server, and if the resume landed last the campaign is Active with nobody behind it.
    // Pausing again is harmless if the pause won; it is the only fix if it lost.
    try {
      await dispatch('campaign/goOfflineSoftphone', null, { root: true })
    } catch (pauseError) {
      console.error('Failed to re-pause the campaign after a wrap-up resume raced going offline', pauseError)
    }
  },

  /**
   * A transport error can mean the registration is gone (a dead websocket, a failed re-register),
   * and the same reasoning as handleUnregistered applies: a live campaign would go on having calls
   * routed at a browser that cannot take them. The campaign is paused first, then the error is
   * restored on top of the reset so the agent is left looking at what happened.
   */
  async handleTransportError({ commit, dispatch }, { message } = {}) {
    stopRinging()

    if (isGoingOffline) {
      commit('SET_LAST_ERROR', message || 'Softphone error')
      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)

      return
    }

    await dispatch('goOffline')

    commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
    commit('SET_LAST_ERROR', message || 'Softphone error')
  },

  /**
   * A newer widget claimed the softphone. Drop this one without re-claiming, so the agent has
   * exactly one live widget.
   *
   * Deliberately does NOT pause the campaign: the new owner is authoritative and may be going live
   * for the very same campaign, so pausing here would fight it.
   */
  async handleTabClaimLost({ commit }) {
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()
    stopRinging()

    if (transport) {
      const currentTransport = transport

      transport = null

      // Listeners go BEFORE the disconnect, not after. Both transports emit UNREGISTERED from
      // their own `disconnect()`, and `handleUnregistered` takes the agent offline - which would
      // pause the campaign the new owner may have just gone live for, and leave this window
      // showing a lost-connection error for a handover that worked.
      currentTransport.removeAllListeners()

      try {
        await currentTransport.disconnect()
      } catch (error) {
        console.error('Failed to disconnect softphone transport', error)
      }

      removeSoftphoneDevHook()
    }

    commit('RESET_SOFTPHONE')
    commit('SET_STATUS', SOFTPHONE_STATUS.OTHER_TAB)
  }
}

/**
 * @type {import('vuex').MutationTree<typeof state>}
 */
const mutations = {
  SET_ROLE(state, role) {
    state.role = role
  },

  /**
   * Applies a snapshot broadcast by the owner. Deliberately does NOT touch `role`: a mirror that
   * adopted the owner's role would start believing it may answer calls.
   */
  APPLY_REMOTE_STATE(state, snapshot) {
    state.status = snapshot.status
    state.liveCampaign = snapshot.liveCampaign
    state.incomingCall = snapshot.incomingCall
    state.activeCall = snapshot.activeCall
    state.wrapUpEndsAt = snapshot.wrapUpEndsAt
    state.isPausedForWrapUp = Boolean(snapshot.isPausedForWrapUp)
    state.isMuted = snapshot.isMuted
    state.lastError = snapshot.lastError
  },

  SET_STATUS(state, status) {
    state.status = status
  },

  SET_LIVE_CAMPAIGN(state, campaign) {
    state.liveCampaign = campaign
  },

  SET_INCOMING_CALL(state, call) {
    state.incomingCall = call
  },

  SET_ACTIVE_CALL(state, call) {
    state.activeCall = call
  },

  SET_WRAP_UP_ENDS_AT(state, wrapUpEndsAt) {
    state.wrapUpEndsAt = wrapUpEndsAt
  },

  SET_PAUSED_FOR_WRAP_UP(state, isPausedForWrapUp) {
    state.isPausedForWrapUp = isPausedForWrapUp
  },

  SET_MUTED(state, isMuted) {
    state.isMuted = isMuted
  },

  SET_LAST_ERROR(state, lastError) {
    state.lastError = lastError
  },

  // `role` is deliberately untouched: resetting the call state does not turn the widget window
  // into a dashboard tab.
  RESET_SOFTPHONE(state) {
    state.status = SOFTPHONE_STATUS.OFFLINE
    state.liveCampaign = null
    state.incomingCall = null
    state.activeCall = null
    state.wrapUpEndsAt = null
    state.isPausedForWrapUp = false
    state.isMuted = false
  }
}

// Test seam: lets a spec inject a FakeSoftphoneTransport without going through `goOnline`.
export const __testing = {
  setTransport(nextTransport) {
    transport = nextTransport
  },

  getTransport() {
    return transport
  },

  setTabLock(nextTabLock) {
    tabLock = nextTabLock
  },

  getTabLock() {
    return tabLock
  },

  hasPendingWrapUpTimer() {
    return wrapUpTimerId !== null
  },

  hasPendingWrapUpEligibilityTimer() {
    return wrapUpEligibilityTimerId !== null
  },

  setChannel(nextChannel) {
    channel = nextChannel
  },

  getChannel() {
    return channel
  },

  getPendingCommand() {
    return pendingCommand
  },

  reset() {
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()
    transport = null
    tabLock = null
    channel = null
    pendingCommand = null
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
